"use client";

import { useEffect, useRef, useState } from "react";
import type { User } from "firebase/auth";
import type {
  MechanicRecord,
  PaymentMethod,
  ServiceRequestRecord,
  TrackingPoint,
  TripRecord,
} from "./firebase-data";
import { startAppCheck } from "./firebase";
import {
  confirmAssignedPartner,
  getRelayContact,
  protectedDispatchConfigured,
} from "./firebase-dispatch";
import {
  ACTIVE_STATUSES,
  cancelRequest,
  confirmRequest,
  markPaymentCollected,
  revertPaymentCollected,
  distanceKmBetween,
  shareMyContact,
  startCancelFraudWatch,
  startLocationPublisher,
  watchContacts,
  watchLiveLocations,
  type ContactCard,
  type Coordinates,
} from "./dispatch-client";
import MapView from "./map-view";
import PartnerDashboard from "./partner-dashboard";
import { onlinePaymentsConfigured, startRazorpayCheckout } from "./firebase-payment";
import { uploadPartnerKycDocument } from "./firebase-kyc";
import PartnerAlertBar from "./partner-alert-bar";
import JobStepGuide from "./job-step-guide";
import PwaInstallBanner from "./pwa-install-banner";
import { customerGuide, liveReach } from "./job-flow";
import { isStaffRole, watchMyRole, type AppRole } from "./admin";
import KycMediaField, { type KycMediaValue } from "./kyc-media-field";
import AddressAutocomplete from "./address-autocomplete";
import {
  PARTNER_DRAFT_KEY,
  isValidUpiId,
  normalizeUpiId,
  runAutoVerification,
} from "./kyc-validation";
import { clearKycMedia, loadKycMedia, saveKycMedia } from "./kyc-media-store";
import {
  POLICY_VERSION,
  activateAutoVerifiedPartner,
  createPaymentIntent,
  createSafetyAlert,
  createServiceRequest,
  cancelMyBroadcastingRequests,
  ensureGuestSession,
  fetchTripResumeDetails,
  watchMyTrips,
  authErrorMessage,
  loginWithEmail,
  loginWithGoogle,
  consumeGoogleRedirectResult,
  logoutUser,
  registerWithEmail,
  recordConsent,
  refreshCurrentUser,
  resetPassword,
  saveUserProfile,
  sendVerificationEmail,
  sendRequestMessage,
  submitPartnerApplication,
  submitPrivacyRequest,
  submitSupportTicket,
  watchAuth,
  watchRequestMessages,
  watchRequestTracking,
  watchServiceRequest,
  watchVerifiedMechanics,
} from "./firebase-data";

type Vehicle = "Car" | "Bike" | "Scooter";
type Stage = "request" | "matching" | "accepted" | "tracking";
type PolicyDoc = "terms" | "privacy" | "cancellation" | "safety" | "partner";
type AppMode = "customer" | "partner" | "admin";

const SERVICE_OPTIONS = ["Puncture", "Battery", "Breakdown", "Towing"] as const;
const VEHICLE_OPTIONS = ["Car", "Bike", "Scooter"] as const;

function formatTripStatus(status: string) {
  return status.replace(/_/g, " ");
}

function tripNeedsAction(status: string) {
  return ["partner_accepted", "broadcasting", "confirmed", "booked", "en_route", "arrived", "in_progress"].includes(status);
}

function tripActionHint(trip: TripRecord) {
  if (trip.status === "partner_accepted") return `Tap to agree ₹${trip.price || 299} with ${trip.helperName || "mistri"}`;
  if (trip.status === "broadcasting") return "Tap to view live broadcast";
  if (["confirmed", "booked", "en_route", "arrived", "in_progress"].includes(trip.status)) return "Tap to track live";
  return "";
}

function maskIdNumber(raw: string, idType: string) {
  const clean = raw.replace(/\s+/g, "").toUpperCase();
  if (!clean) return "";
  if (idType === "aadhaar") {
    const digits = clean.replace(/\D/g, "");
    return digits.length >= 4 ? `XXXX-XXXX-${digits.slice(-4)}` : clean.slice(0, 14);
  }
  if (clean.length <= 4) return clean;
  return `${"X".repeat(Math.min(6, clean.length - 4))}${clean.slice(-4)}`;
}

function maskAccountNumber(raw: string) {
  const digits = raw.replace(/\D/g, "");
  if (digits.length < 4) return digits;
  return `${"X".repeat(Math.max(0, digits.length - 4))}${digits.slice(-4)}`;
}

const useClientDispatch = !protectedDispatchConfigured;

const fallbackHelpers: MechanicRecord[] = [
  { id: "waiting", name: "Waiting for nearby mistri", eta: "—", distance: "—", rating: "—", jobs: "—", price: 299, color: "#ffcf40", initials: "MB" },
];

const problems = [
  { icon: "✦", label: "Puncture", hint: "Flat tyre" },
  { icon: "▰", label: "Battery", hint: "Jump start" },
  { icon: "⚙", label: "Breakdown", hint: "Car won’t start" },
  { icon: "⌁", label: "Towing", hint: "Tow my vehicle" },
];

const policyContent: Record<PolicyDoc, { title: string; eyebrow: string; sections: Array<{ heading: string; text: string }> }> = {
  terms: {
    eyebrow: "BOOKING TERMS",
    title: "Clear terms before help starts",
    sections: [
      { heading: "Marketplace role", text: "Mistry Bhai connects customers with independent roadside-service partners. The selected partner performs and is responsible for the on-road service." },
      { heading: "Price & approval", text: "The shown amount covers the listed visit and labour. Any tyre, tube, battery, towing distance, tax or extra part must be quoted and approved in the chat before work begins." },
      { heading: "Emergency use", text: "Mistry Bhai is roadside assistance, not police, ambulance or fire service. If anyone is in immediate danger, move to a safe place and call 112." },
      { heading: "Fair use", text: "False requests, abusive conduct, unsafe instructions, off-platform payment fraud and misuse of partner information are prohibited." },
    ],
  },
  privacy: {
    eyebrow: "PRIVACY NOTICE",
    title: "Only the data needed to send help",
    sections: [
      { heading: "What we collect", text: "Account details, live location when permitted, vehicle/issue details, booking activity, chat, payment status, support and safety records." },
      { heading: "Why we use it", text: "To match a nearby partner, fulfil the booking, prevent fraud, provide support, maintain invoices and meet legal obligations. We do not sell personal data." },
      { heading: "Sharing & retention", text: "The assigned partner receives the minimum booking and location data needed to serve you. Records are retained only for service, safety, tax, dispute and legal needs, then deleted or anonymised." },
      { heading: "Your choices", text: "You can withdraw optional permissions and submit access, correction or deletion requests from Account. Emergency and legal records may need to be retained for a limited period." },
    ],
  },
  cancellation: {
    eyebrow: "CANCELLATION & REFUND",
    title: "No surprise cancellation charges",
    sections: [
      { heading: "Before dispatch", text: "Cancel free before a partner accepts. No charge while the request is still broadcasting." },
      { heading: "After accept / confirm", text: "If you cancel after a mistri accepts, we keep watching their GPS briefly. Travelling to your pin after an off-app deal triggers suspension, subscription cancel and deduction of the agreed job amount." },
      { heading: "After work starts", text: "Approved work already completed and installed parts are chargeable. Unperformed prepaid service amounts are refundable after review." },
      { heading: "Refund timing", text: "Approved online refunds return to the original payment method according to the payment provider or bank timeline. Cash disputes are handled through support." },
    ],
  },
  safety: {
    eyebrow: "SAFETY STANDARD",
    title: "Verification is a process, not a badge",
    sections: [
      { heading: "Partner checks", text: "Partners remain offline until identity, selfie/liveness, phone, service area and workshop or work-history checks are reviewed." },
      { heading: "During a trip", text: "Booking, agreed price, chat and status are recorded. Keep communication inside the app and verify the partner name before service." },
      { heading: "Prohibited conduct", text: "Harassment, discrimination, intoxicated service, unsafe repairs, counterfeit parts and unapproved price changes can result in immediate suspension." },
      { heading: "Emergency", text: "Use SOS to create a priority safety record. For immediate danger call India emergency number 112; in-app support cannot replace emergency services." },
    ],
  },
  partner: {
    eyebrow: "PARTNER CODE",
    title: "Professional service on every road",
    sections: [
      { heading: "Authenticity", text: "Provide accurate identity and business information, keep documents current, and never allow another person to use your partner account." },
      { heading: "Transparent work", text: "Explain the fault, quote every additional part before fitting it, use lawful genuine parts, and issue a service record." },
      { heading: "Customer safety", text: "Respect personal space and location privacy, follow road-safety rules, and never contact a customer after the job except through approved support." },
      { heading: "Payouts & disputes", text: "Payout eligibility depends on completed verification, recorded job status and payment reconciliation. Fraudulent or unsafe jobs may be withheld for review." },
    ],
  },
};

export default function Home() {
  const [vehicle, setVehicle] = useState<Vehicle>("Car");
  const [problem, setProblem] = useState("Puncture");
  const [stage, setStage] = useState<Stage>("request");
  const [selected, setSelected] = useState(0);
  const [helpers, setHelpers] = useState<MechanicRecord[]>(fallbackHelpers);
  const [chatOpen, setChatOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState<Array<{ mine: boolean; text: string }>>([]);
  const [toast, setToast] = useState("");
  const [user, setUser] = useState<User | null>(null);
  const [requestId, setRequestId] = useState("");
  const [assignedRequest, setAssignedRequest] = useState<ServiceRequestRecord | null>(null);
  const [trackingPoint, setTrackingPoint] = useState<TrackingPoint | null>(null);
  const [relayPhone, setRelayPhone] = useState<string | null>(null);
  const [contacts, setContacts] = useState<Record<string, ContactCard>>({});
  const [partnerLivePoint, setPartnerLivePoint] = useState<Coordinates | null>(null);
  const [phoneForm, setPhoneForm] = useState("");
  const [appMode, setAppMode] = useState<AppMode>("customer");
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cloudState, setCloudState] = useState<"connecting" | "live" | "offline">("connecting");
  const [locationLabel, setLocationLabel] = useState("Use your live location");
  const [coordinates, setCoordinates] = useState<{ latitude: number; longitude: number } | null>(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<"signin" | "signup">("signup");
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [authBusy, setAuthBusy] = useState(false);
  const [tripsOpen, setTripsOpen] = useState(false);
  const [trips, setTrips] = useState<TripRecord[]>([]);
  const [pendingTrip, setPendingTrip] = useState<TripRecord | null>(null);
  const [viewingHome, setViewingHome] = useState(false);
  const [partnerOpen, setPartnerOpen] = useState(false);
  const [userRole, setUserRole] = useState<AppRole>("customer");
  const [partnerBusy, setPartnerBusy] = useState(false);
  const [partnerForm, setPartnerForm] = useState({
    name: "",
    phone: "",
    email: "",
    dateOfBirth: "",
    alternatePhone: "",
    addressLine: "",
    city: "",
    state: "",
    pincode: "",
    shopName: "",
    experienceYears: "2",
    services: ["Puncture", "Battery"] as string[],
    vehicleTypes: ["Car", "Bike"] as string[],
    workType: "mobile" as "mobile" | "workshop" | "both",
    serviceRadiusKm: "10",
    idType: "aadhaar" as "aadhaar" | "dl" | "voter" | "pan",
    idNumber: "",
    panNumber: "",
    dlNumber: "",
    gstin: "",
    accountHolderName: "",
    accountNumber: "",
    ifsc: "",
    upiId: "",
    emergencyName: "",
    emergencyPhone: "",
    acceptedPartnerCode: false,
  });
  const [idDocMedia, setIdDocMedia] = useState<KycMediaValue>(null);
  const [selfieMedia, setSelfieMedia] = useState<KycMediaValue>(null);
  const [workshopMedia, setWorkshopMedia] = useState<KycMediaValue>(null);
  const [upiHint, setUpiHint] = useState("");
  const [kycFailList, setKycFailList] = useState<string[]>([]);
  const [signupConsent, setSignupConsent] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | "razorpay">("upi_on_service");
  const [bookingConsent, setBookingConsent] = useState(false);
  const [policyOpen, setPolicyOpen] = useState<PolicyDoc | null>(null);
  const [supportOpen, setSupportOpen] = useState(false);
  const [supportForm, setSupportForm] = useState({ category: "booking", message: "" });
  const [privacyBusy, setPrivacyBusy] = useState(false);
  const stopPublisherRef = useRef<(() => void) | null>(null);
  const stopFraudWatchRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 3200);
    return () => clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    void startAppCheck().catch(() => undefined);
    const stopWatching = watchAuth((nextUser) => {
      setUser(nextUser);
      setCloudState(nextUser ? "live" : "connecting");
      if (nextUser) void saveUserProfile(nextUser).catch(() => undefined);
    });
    // Let a returning Google redirect land before falling back to a guest session.
    void consumeGoogleRedirectResult()
      .then((redirectUser) => {
        if (redirectUser) {
          setAuthOpen(false);
          setToast("Signed in with Google");
        }
      })
      .finally(() => {
        void ensureGuestSession().catch(() => setCloudState("offline"));
      });
    return stopWatching;
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (new URLSearchParams(window.location.search).get("mode") === "partner") {
      setAppMode("partner");
    }
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === "OPEN_PARTNER_DASHBOARD") setAppMode("partner");
    };
    navigator.serviceWorker?.addEventListener("message", onMessage);
    return () => navigator.serviceWorker?.removeEventListener("message", onMessage);
  }, []);

  useEffect(() => {
    if (!user || user.isAnonymous) {
      setUserRole("customer");
      return;
    }
    return watchMyRole(setUserRole);
  }, [user]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(PARTNER_DRAFT_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw) as { form?: typeof partnerForm };
      if (draft.form) setPartnerForm((prev) => ({ ...prev, ...draft.form }));
    } catch {
      // ignore corrupt draft
    }
    void Promise.all([
      loadKycMedia("id"),
      loadKycMedia("selfie"),
      loadKycMedia("workshop"),
    ]).then(([id, selfie, workshop]) => {
      if (id) setIdDocMedia(id);
      if (selfie) setSelfieMedia(selfie);
      if (workshop) setWorkshopMedia(workshop);
    }).catch(() => undefined);
  }, []);

  useEffect(() => { void saveKycMedia("id", idDocMedia).catch(() => undefined); }, [idDocMedia]);
  useEffect(() => { void saveKycMedia("selfie", selfieMedia).catch(() => undefined); }, [selfieMedia]);
  useEffect(() => { void saveKycMedia("workshop", workshopMedia).catch(() => undefined); }, [workshopMedia]);

  const savePartnerDraft = () => {
    try {
      localStorage.setItem(PARTNER_DRAFT_KEY, JSON.stringify({ form: partnerForm, savedAt: Date.now() }));
      void Promise.all([
        saveKycMedia("id", idDocMedia),
        saveKycMedia("selfie", selfieMedia),
        saveKycMedia("workshop", workshopMedia),
      ]);
      setToast("Draft saved on this device, including attached photos");
    } catch {
      setToast("Could not save draft on this device");
    }
  };

  useEffect(() => {
    if (!user) return;
    return watchVerifiedMechanics((liveHelpers) => {
      if (liveHelpers.length) setHelpers(liveHelpers);
    });
  }, [user]);

  useEffect(() => {
    if (!requestId || viewingHome) return;
    return watchServiceRequest(requestId, (nextRequest) => {
      setAssignedRequest(nextRequest);
      if (nextRequest.partnerId) {
        const helperIndex = helpers.findIndex((helper) => helper.id === nextRequest.partnerId);
        if (helperIndex >= 0) setSelected(helperIndex);
      }
      if (nextRequest.status === "partner_accepted") setStage("accepted");
      if (["confirmed", "booked", "en_route", "arrived", "in_progress", "completed"].includes(nextRequest.status)) {
        setStage("tracking");
      }
      if (nextRequest.status === "cancelled") {
        setStage("request");
        setToast("Booking cancelled");
      }
      if (nextRequest.status === "no_partners") {
        setToast("No nearby partner accepted yet — keep waiting or widen your search later");
      }
    });
  }, [helpers, requestId, viewingHome]);

  useEffect(() => {
    if (!requestId || (stage !== "accepted" && stage !== "tracking")) return;
    return watchRequestTracking(requestId, setTrackingPoint);
  }, [requestId, stage]);

  useEffect(() => {
    if (!requestId || stage !== "tracking" || !useClientDispatch) return;
    const unlocked = assignedRequest?.status
      ? ["confirmed", "booked", "en_route", "arrived", "in_progress", "completed"].includes(assignedRequest.status)
      : false;
    if (!unlocked) return;
    const stopContacts = watchContacts(requestId, setContacts);
    const stopLocations = watchLiveLocations(requestId, (points) => {
      const partnerEntry = Object.entries(points).find(([, point]) => point.role === "partner");
      setPartnerLivePoint(partnerEntry
        ? { latitude: partnerEntry[1].latitude, longitude: partnerEntry[1].longitude }
        : null);
    });
    return () => {
      stopContacts();
      stopLocations();
    };
  }, [requestId, stage, assignedRequest?.status]);

  useEffect(() => {
    const liveStatus = assignedRequest?.status || "";
    const shouldPublish = useClientDispatch
      && !!requestId
      && ["partner_accepted", ...ACTIVE_STATUSES].includes(liveStatus);
    if (!shouldPublish) {
      stopPublisherRef.current?.();
      stopPublisherRef.current = null;
      return;
    }
    stopPublisherRef.current?.();
    stopPublisherRef.current = startLocationPublisher(requestId, "customer");
    return () => {
      stopPublisherRef.current?.();
      stopPublisherRef.current = null;
    };
  }, [requestId, assignedRequest?.status]);

  useEffect(() => {
    if (stage !== "tracking" || !requestId || !protectedDispatchConfigured) return;
    void getRelayContact(requestId)
      .then(setRelayPhone)
      .catch(() => setRelayPhone(null));
  }, [requestId, stage]);

  useEffect(() => {
    if (!chatOpen || !requestId) return;
    return watchRequestMessages(requestId, (cloudMessages) => {
      if (!cloudMessages.length) return;
      setMessages(cloudMessages.map((item) => ({
        mine: item.senderId === user?.uid,
        text: item.text,
      })));
    });
  }, [chatOpen, requestId, user?.uid]);

  const readLiveLocation = () => new Promise<{ latitude: number; longitude: number }>((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Location is not available on this device"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({ latitude: position.coords.latitude, longitude: position.coords.longitude }),
      () => reject(new Error("Location permission is needed to find a nearby bhai")),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 15000 },
    );
  });

  const startSearch = async () => {
    setViewingHome(false);
    setStage("matching");
    setAssignedRequest(null);
    setContacts({});
    setPartnerLivePoint(null);
    setRelayPhone(null);
    try {
      const liveCoordinates = coordinates || await readLiveLocation();
      if (!coordinates) {
        setCoordinates(liveCoordinates);
        setLocationLabel("Live location locked");
      }
      await cancelMyBroadcastingRequests().catch(() => undefined);
      const id = await createServiceRequest({
        vehicle,
        problem,
        locationLabel: coordinates ? locationLabel : "Live location locked",
        coordinates: liveCoordinates,
      });
      setRequestId(id);
      setCloudState("live");
      setToast(`Alerting nearby mistris · ${vehicle} · ${problem}`);
    } catch {
      setStage("request");
      setToast("Allow live location and retry — nearby matching needs your position");
    }
  };

  const cancelCurrentRequest = async (reason: string) => {
    if (!requestId || cancelBusy) return;
    setCancelBusy(true);
    const partnerId = assignedRequest?.partnerId;
    const agreedPrice = assignedRequest?.price || 299;
    const customerLocation = coordinates;
    const distanceAtCancel = partnerLivePoint && customerLocation
      ? distanceKmBetween(partnerLivePoint, customerLocation)
      : assignedRequest?.partnerDistanceKm || 2;
    try {
      await cancelRequest(requestId, reason);
      setStage("request");
      setToast("Request cancelled");
      if (useClientDispatch && partnerId && customerLocation && ["partner_accepted", ...ACTIVE_STATUSES].includes(assignedRequest?.status || "")) {
        stopFraudWatchRef.current?.();
        stopFraudWatchRef.current = startCancelFraudWatch({
          requestId,
          partnerId,
          customerLocation,
          distanceAtCancelKm: distanceAtCancel,
          agreedPrice,
          onVerdict: (verdict) => {
            if (verdict.flagged) {
              setToast("Off-app trip detected — mistri suspended, subscription cancelled, job amount deducted");
            }
            stopFraudWatchRef.current = null;
          },
        });
        setToast("Cancelled — we will watch if that mistri still approaches your pin");
      }
      setRequestId("");
      setAssignedRequest(null);
    } catch {
      setToast("Could not cancel — try again");
    } finally {
      setCancelBusy(false);
    }
  };

  const goHome = () => {
    setAppMode("customer");
    setViewingHome(true);
    setStage("request");
    setTripsOpen(false);
    setPaymentOpen(false);
    setChatOpen(false);
    setPartnerOpen(false);
    setAuthOpen(false);
    setSupportOpen(false);
    setPolicyOpen(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (assignedRequest?.status === "completed" || assignedRequest?.status === "cancelled" || !requestId) {
      setRequestId("");
      setAssignedRequest(null);
      setContacts({});
      setPartnerLivePoint(null);
      setViewingHome(false);
    }
  };

  const bookHelper = async () => {
    if (!bookingConsent) {
      setToast("Accept the booking and cancellation terms");
      return;
    }
    if (!requestId) {
      setToast("Create a live request first");
      return;
    }
    if (assignedRequest?.status !== "partner_accepted" && useClientDispatch) {
      setToast("Wait for a mistri to accept with a locked price");
      return;
    }
    if (paymentMethod === "razorpay") {
      if (!user || user.isAnonymous || !user.emailVerified) {
        setPaymentOpen(false);
        setAuthOpen(true);
        setToast("Sign in and verify your email before paying online");
        return;
      }
      setPaymentOpen(false);
      setToast("Opening protected payment…");
      try {
        const payment = await startRazorpayCheckout({
          requestId,
          mechanicId: activeHelper.id,
          description: `${problem} roadside help · ${vehicle}`,
        });
        if (payment.paid) {
          if (useClientDispatch) await confirmRequest(requestId, "upi_on_service");
          else await confirmAssignedPartner(requestId);
          setStage("tracking");
          setToast("Payment confirmed — your bhai is on the way");
        } else {
          setStage("accepted");
          setToast("Payment verified — waiting for bank capture");
        }
      } catch (error) {
        setStage("accepted");
        setToast(error instanceof Error ? error.message : "Payment was not completed");
      }
      return;
    }
    setPaymentOpen(false);
    try {
      await recordConsent("booking");
      if (paymentMethod === "cash" || paymentMethod === "upi_on_service") {
        await createPaymentIntent({
          requestId,
          amount: activeHelper.price,
          method: paymentMethod,
        }).catch(() => undefined);
      }
      if (useClientDispatch) {
        await confirmRequest(requestId, paymentMethod === "cash" ? "cash" : "upi_on_service");
      } else await confirmAssignedPartner(requestId);
      setStage("tracking");
      setToast(`Price ₹${activeHelper.price} agreed — numbers unlock, live map on`);
    } catch {
      setStage("accepted");
      setToast("Confirmation did not sync — price agreement was not unlocked");
    }
  };

  const shareCustomerPhone = async () => {
    if (!requestId) return;
    try {
      await shareMyContact(requestId, phoneForm, user?.displayName || "Customer");
      setToast("Your number is now visible to the mistri");
    } catch (error) {
      setToast(error instanceof Error ? error.message : "Share number only after price confirmation");
    }
  };

  const sendMessage = async () => {
    if (!message.trim()) return;
    const outgoing = message.trim();
    if (requestId) {
      try {
        await sendRequestMessage(requestId, outgoing);
        setMessages((old) => [...old, { mine: true, text: outgoing }]);
        setMessage("");
      } catch (error) {
        setToast(error instanceof Error ? error.message : "Message could not be sent");
      }
    }
  };

  const useMyLocation = () => {
    void readLiveLocation()
      .then((nextCoordinates) => {
        setCoordinates(nextCoordinates);
        setLocationLabel("Live location locked");
        setToast("Live location locked");
      })
      .catch((error) => setToast(error instanceof Error ? error.message : "Location permission not granted"));
  };

  const triggerSos = async () => {
    setToast("SOS logged — your safety contact can be notified");
    try {
      await createSafetyAlert(locationLabel, requestId || undefined);
    } catch {
      setToast("Call 112 if you are in immediate danger");
    }
  };

  useEffect(() => {
    if (!tripsOpen || !user) return;
    return watchMyTrips(setTrips);
  }, [tripsOpen, user]);

  useEffect(() => {
    if (!user || user.isAnonymous || requestId || appMode !== "customer") {
      setPendingTrip(null);
      return;
    }
    return watchMyTrips((list) => {
      setPendingTrip(list.find((trip) => trip.status === "partner_accepted") || null);
    });
  }, [user, requestId, appMode]);

  const resumeTrip = async (trip: TripRecord) => {
    if (!tripNeedsAction(trip.status)) {
      setToast(trip.status === "completed" ? "This trip is already completed" : "This trip was cancelled");
      return;
    }
    setTripsOpen(false);
    try {
      const details = await fetchTripResumeDetails(trip.id);
      if (!details) {
        setToast("Trip not found — it may have expired");
        return;
      }
      setRequestId(details.id);
      setViewingHome(false);
      setVehicle(details.vehicle as Vehicle);
      setProblem(details.problem);
      setLocationLabel(details.locationLabel);
      if (details.coordinates) setCoordinates(details.coordinates);
      if (details.status === "broadcasting") setStage("matching");
      else if (details.status === "partner_accepted") setStage("accepted");
      else if (["confirmed", "booked", "en_route", "arrived", "in_progress"].includes(details.status)) setStage("tracking");
      setCloudState("live");
      window.scrollTo({ top: 0, behavior: "smooth" });
      if (details.status === "partner_accepted") {
        setToast(`Agree ₹${details.price || 299} to confirm ${details.helperName || "your mistri"}`);
      } else {
        setToast("Trip reopened — continue where you left off");
      }
    } catch {
      setToast("Could not open this trip — try again");
    }
  };

  const openTrips = async () => {
    if (!user || user.isAnonymous) {
      setAuthOpen(true);
      setToast("Sign in to see trips across devices");
      return;
    }
    setTripsOpen(true);
  };

  const submitAuth = async () => {
    const strongPassword = authPassword.length >= 8
      && /[a-z]/.test(authPassword)
      && /[A-Z]/.test(authPassword)
      && /\d/.test(authPassword);
    if (!authEmail || (authMode === "signup" && !strongPassword) || (authMode === "signin" && !authPassword)) {
      setToast(authMode === "signup" ? "Use 8+ characters with upper, lower and a number" : "Enter your email and password");
      return;
    }
    if (authMode === "signup" && !signupConsent) {
      setToast("Review and accept the Terms and Privacy Notice");
      return;
    }
    setAuthBusy(true);
    try {
      const signedInUser = authMode === "signup"
        ? await registerWithEmail(authEmail, authPassword)
        : await loginWithEmail(authEmail, authPassword);
      // Sign-in already succeeded here, so profile sync must never surface as a login failure.
      await saveUserProfile(signedInUser).catch(() => undefined);
      if (authMode === "signup") {
        await recordConsent("account").catch(() => undefined);
        await sendVerificationEmail().catch(() => undefined);
      }
      setAuthOpen(false);
      setToast(authMode === "signup" ? "Account created — trips are now protected" : "Welcome back");
    } catch (error) {
      setToast(authErrorMessage(error, "Could not sign in. Check details and retry."));
    } finally {
      setAuthBusy(false);
    }
  };

  const googleAuth = async () => {
    setAuthBusy(true);
    try {
      const signedInUser = await loginWithGoogle();
      if (!signedInUser) {
        setToast("Taking you to Google…");
        return;
      }
      await saveUserProfile(signedInUser).catch(() => undefined);
      await recordConsent("account").catch(() => undefined);
      setAuthOpen(false);
      setToast("Signed in with Google");
    } catch (error) {
      setToast(authErrorMessage(error, "Google sign-in was not completed"));
    } finally {
      setAuthBusy(false);
    }
  };

  const applyAsPartner = async () => {
    if (!user || user.isAnonymous) {
      setPartnerOpen(false);
      setAuthOpen(true);
      setToast("Sign in first — partner KYC must be tied to a real account");
      return;
    }
    const upi = normalizeUpiId(partnerForm.upiId);
    const verification = runAutoVerification({
      name: partnerForm.name,
      phone: partnerForm.phone,
      email: partnerForm.email,
      dateOfBirth: partnerForm.dateOfBirth,
      addressLine: partnerForm.addressLine,
      city: partnerForm.city,
      state: partnerForm.state,
      pincode: partnerForm.pincode,
      shopName: partnerForm.shopName,
      idNumber: partnerForm.idNumber,
      panNumber: partnerForm.panNumber,
      ifsc: partnerForm.ifsc,
      upiId: upi,
      accountHolderName: partnerForm.accountHolderName,
      accountNumber: partnerForm.accountNumber,
      emergencyName: partnerForm.emergencyName,
      emergencyPhone: partnerForm.emergencyPhone,
      services: partnerForm.services,
      vehicleTypes: partnerForm.vehicleTypes,
      hasIdDoc: !!idDocMedia?.file,
      hasSelfie: !!selfieMedia?.file,
      selfieFromCamera: selfieMedia?.source === "camera",
      acceptedPartnerCode: partnerForm.acceptedPartnerCode,
    });
    if (!verification.ok) {
      const failed = verification.checks.filter((check) => !check.ok).map((check) => check.label);
      setKycFailList(failed);
      setToast(`Still needed: ${failed.join(" · ")}`);
      document.getElementById("government-id-photo-pdf")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setKycFailList([]);
    setPartnerBusy(true);
    try {
      await recordConsent("partner");
      const idDocumentPath = await uploadPartnerKycDocument(idDocMedia!.file, "id_proof");
      const selfiePath = await uploadPartnerKycDocument(selfieMedia!.file, "selfie");
      const workshopPhotoPath = workshopMedia?.file
        ? await uploadPartnerKycDocument(workshopMedia.file, "workshop")
        : "";
      await submitPartnerApplication({
        name: partnerForm.name.trim(),
        phone: partnerForm.phone.trim(),
        email: partnerForm.email.trim(),
        dateOfBirth: partnerForm.dateOfBirth,
        alternatePhone: partnerForm.alternatePhone.trim(),
        addressLine: partnerForm.addressLine.trim(),
        city: partnerForm.city.trim(),
        state: partnerForm.state.trim(),
        pincode: partnerForm.pincode.trim(),
        shopName: partnerForm.shopName.trim(),
        experienceYears: Math.max(0, Number(partnerForm.experienceYears) || 0),
        services: partnerForm.services,
        vehicleTypes: partnerForm.vehicleTypes,
        workType: partnerForm.workType,
        serviceRadiusKm: Math.max(3, Math.min(40, Number(partnerForm.serviceRadiusKm) || 10)),
        idType: partnerForm.idType,
        idNumberMasked: maskIdNumber(partnerForm.idNumber, partnerForm.idType),
        panNumber: partnerForm.panNumber.trim().toUpperCase(),
        dlNumber: partnerForm.dlNumber.trim().toUpperCase(),
        gstin: partnerForm.gstin.trim().toUpperCase(),
        accountHolderName: partnerForm.accountHolderName.trim(),
        accountNumberMasked: maskAccountNumber(partnerForm.accountNumber),
        ifsc: partnerForm.ifsc.trim().toUpperCase(),
        upiId: upi,
        emergencyName: partnerForm.emergencyName.trim(),
        emergencyPhone: partnerForm.emergencyPhone.trim(),
        idDocumentPath,
        selfiePath,
        workshopPhotoPath,
        idDocSource: idDocMedia!.source,
        selfieSource: selfieMedia!.source,
        autoVerified: true,
        verificationChecks: verification.checks,
        acceptedPartnerCode: true,
      });
      await activateAutoVerifiedPartner({
        name: partnerForm.name.trim() || partnerForm.shopName.trim(),
        city: partnerForm.city.trim(),
        services: partnerForm.services,
        vehicleTypes: partnerForm.vehicleTypes,
        workType: partnerForm.workType,
        serviceRadiusKm: Math.max(3, Math.min(40, Number(partnerForm.serviceRadiusKm) || 10)),
        upiId: upi,
      });
      localStorage.removeItem(PARTNER_DRAFT_KEY);
      await clearKycMedia().catch(() => undefined);
      setPartnerOpen(false);
      setToast("All checks passed — partner auto-approved. Open Mistri app to go online.");
      setAppMode("partner");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Application could not sync";
      if (/storage|firebase storage|bucket|404|not.?found|cors/i.test(message)) {
        setToast("Enable Firebase Storage (Get Started in console), then retry document upload");
      } else {
        setToast(message);
      }
    } finally {
      setPartnerBusy(false);
    }
  };

  const createSupportCase = async () => {
    if (supportForm.message.trim().length < 10) {
      setToast("Please add at least 10 characters");
      return;
    }
    try {
      const ticketId = await submitSupportTicket({
        category: supportForm.category,
        message: supportForm.message.trim(),
        requestId: requestId || undefined,
      });
      setSupportForm({ category: "booking", message: "" });
      setSupportOpen(false);
      setToast(`Support case ${ticketId.slice(0, 6).toUpperCase()} created`);
    } catch {
      setToast("Could not create the support case — please retry");
    }
  };

  const requestPrivacyAction = async (type: "access" | "correction" | "deletion") => {
    if (!user || user.isAnonymous) {
      setPolicyOpen(null);
      setAuthOpen(true);
      setToast("Sign in to submit a verified privacy request");
      return;
    }
    setPrivacyBusy(true);
    try {
      await submitPrivacyRequest(type, `Requested by ${user.email || user.uid} from the account privacy centre.`);
      setToast(`${type[0].toUpperCase()}${type.slice(1)} request received`);
    } catch {
      setToast("Could not submit the privacy request");
    } finally {
      setPrivacyBusy(false);
    }
  };

  const baseHelper = helpers[selected] || fallbackHelpers[0];
  const activeHelper: MechanicRecord = {
    ...baseHelper,
    id: assignedRequest?.partnerId || baseHelper.id,
    name: assignedRequest?.helperName || baseHelper.name,
    initials: assignedRequest?.helperInitials || baseHelper.initials,
    rating: assignedRequest?.helperRating?.toFixed(1) || baseHelper.rating,
    jobs: assignedRequest?.helperJobs !== undefined ? String(assignedRequest.helperJobs) : baseHelper.jobs,
    price: assignedRequest?.price || baseHelper.price,
    eta: assignedRequest?.etaMinutes ? `${assignedRequest.etaMinutes} min` : baseHelper.eta,
    distance: assignedRequest?.partnerDistanceKm !== undefined
      ? `${assignedRequest.partnerDistanceKm.toFixed(1)} km`
      : baseHelper.distance,
  };
  const mechanicCoordinates = partnerLivePoint || trackingPoint || assignedRequest?.partnerCoordinates || null;
  const customerReach = liveReach(coordinates, mechanicCoordinates);
  const displayDistance = customerReach?.distanceLabel ?? activeHelper.distance;
  const displayEta = customerReach?.etaLabel ?? activeHelper.eta;
  const mapsUrl = mechanicCoordinates
    ? `https://www.google.com/maps/search/?api=1&query=${mechanicCoordinates.latitude},${mechanicCoordinates.longitude}`
    : null;
  const partnerContact = assignedRequest?.partnerId ? contacts[assignedRequest.partnerId] : undefined;
  const myContact = user?.uid ? contacts[user.uid] : undefined;
  const numbersUnlocked = assignedRequest?.status
    ? ["confirmed", "en_route", "arrived", "in_progress", "completed"].includes(assignedRequest.status)
    : false;
  const liveStatus = assignedRequest?.status || "confirmed";
  const jobDone = liveStatus === "completed";
  const progressStep = liveStatus === "completed" ? 4
    : liveStatus === "arrived" || liveStatus === "in_progress" ? 3
    : liveStatus === "en_route" ? 2
    : 1;
  const trackCopy = jobDone
    ? { kicker: "SERVICE DONE", title: `Pay ₹${activeHelper.price} now` }
    : liveStatus === "arrived" || liveStatus === "in_progress"
      ? { kicker: liveStatus === "in_progress" ? "WORK IN PROGRESS" : "MISTRI HAS ARRIVED", title: `${activeHelper.name.split(" ")[0]} is with you` }
      : { kicker: "CONFIRMED · HELP IS ON THE WAY", title: `${activeHelper.name.split(" ")[0]} arrives in ${displayEta.replace("~", "")}` };
  const payLabel = assignedRequest?.paymentMethod === "cash" ? "Cash on the job" : "UPI on the job";
  const payCollected = assignedRequest?.paymentStatus === "collected";
  const customerFlow = customerGuide(liveStatus, payCollected, !!assignedRequest?.jobCode);

  if (appMode === "partner") {
    return (
      <main>
        <PartnerDashboard
          user={user}
          onRequireSignIn={() => { setAppMode("customer"); setAuthOpen(true); }}
          onToast={setToast}
          onExit={() => setAppMode("customer")}
        />
        {toast && <div className="toast"><span>✓</span>{toast}</div>}
        <PwaInstallBanner />
      </main>
    );
  }

  if (appMode === "admin") {
    return (
      <main>
        <AdminDashboard
          user={user}
          role={userRole}
          onRequireSignIn={() => { setAppMode("customer"); setAuthOpen(true); }}
          onToast={setToast}
          onExit={() => setAppMode("customer")}
        />
        {toast && <div className="toast"><span>✓</span>{toast}</div>}
        <PwaInstallBanner />
      </main>
    );
  }

  return (
    <main>
      {userRole === "partner" && user && !user.isAnonymous && (
        <PartnerAlertBar
          user={user}
          onOpenDashboard={() => setAppMode("partner")}
          onToast={setToast}
        />
      )}
      <header className="nav">
        <a className="brand" href="#" aria-label="Mistry Bhai home" onClick={(event) => { event.preventDefault(); goHome(); }}>
          <span className="brand-mark"><span>MB</span></span>
          <span>Mistry <b>Bhai</b></span>
        </a>
        <nav aria-label="Main navigation">
          <a href="#how">How it works</a>
          <a href="#safety">Safety</a>
          <button className="trips-link" onClick={openTrips}>My trips</button>
          <button className={userRole === "partner" ? "partner partner-live" : "partner"} onClick={() => setAppMode("partner")}>
            {userRole === "partner" ? "Mistri dashboard" : "Mistri app"}
          </button>
          {userRole !== "partner" && (
            <button className="trips-link" onClick={() => {
              if (user?.email) setPartnerForm((prev) => ({ ...prev, email: prev.email || user.email || "" }));
              if (user?.displayName) setPartnerForm((prev) => ({ ...prev, name: prev.name || user.displayName || "" }));
              setPartnerOpen(true);
            }}>Become a partner</button>
          )}
          {isStaffRole(userRole) && (
            <button className="partner" onClick={() => setAppMode("admin")}>Admin</button>
          )}
        </nav>
        <button className="profile" aria-label="Open profile" onClick={() => setAuthOpen(true)}>
          {user?.isAnonymous ? "G" : (user?.displayName || user?.email || "A").slice(0, 1).toUpperCase()}
        </button>
      </header>

      {pendingTrip && (
        <button type="button" className="pending-trip-banner" onClick={() => void resumeTrip(pendingTrip)}>
          <span className="sos-pulse-dot" />
          <span>
            <b>{pendingTrip.helperName || "A mistri"} accepted at ₹{pendingTrip.price || 299}</b>
            <small>Tap here to agree price and unlock contact</small>
          </span>
          <span className="pending-trip-go">Agree →</span>
        </button>
      )}

      {viewingHome && requestId && assignedRequest && !["cancelled", "completed"].includes(assignedRequest.status) && (
        <button
          type="button"
          className="pending-trip-banner"
          onClick={() => {
            setViewingHome(false);
            if (assignedRequest.status === "partner_accepted") setStage("accepted");
            else if (assignedRequest.status === "broadcasting") setStage("matching");
            else setStage("tracking");
          }}
        >
          <span className="sos-pulse-dot" />
          <span>
            <b>Active trip in progress — {assignedRequest.status.replace(/_/g, " ")}</b>
            <small>Tap to return to live tracking screen</small>
          </span>
          <span className="pending-trip-go">Resume →</span>
        </button>
      )}

      <section className="hero">
        <div className="hero-copy">
          <div className="eyebrow"><span className="live-dot" /> 24×7 roadside help · Nearby only</div>
          <h1>Gaadi ruki?<br /><em>Bhai aa raha hai.</em></h1>
          <p className="lead">Nearby verified mechanic. Pehle fixed price.<br />Live tracking. Help in minutes, not hours.</p>
          <div className="trust-row">
            <div><strong>12 min</strong><span>avg. arrival</span></div>
            <div><strong>4.8/5</strong><span>user rating</span></div>
            <div><strong>Verified</strong><span>nearby mistris</span></div>
          </div>
          <div className="promise"><span>✓</span> Fixed night &amp; peak rates — never dynamic surge</div>
          {userRole === "partner" && (
            <button className="partner-jump" onClick={() => setAppMode("partner")}>
              You are a verified mistri · Open dashboard for new alerts &amp; past visits <span>→</span>
            </button>
          )}
        </div>

        <div className="booking-shell" aria-live="polite">
          {stage === "request" && (
            <div className="request-panel">
              <div className="panel-top">
                <div>
                  <span className="step">STEP 1 OF 2</span>
                  <h2>What happened?</h2>
                </div>
                <div className="panel-status">
                  <span className={`cloud-status ${cloudState}`}><i />{cloudState === "live" ? "Cloud live" : cloudState === "offline" ? "Offline" : "Connecting"}</span>
                  <button className="sos" onClick={triggerSos}>SOS</button>
                </div>
              </div>

              <div className="vehicle-tabs" role="group" aria-label="Select vehicle">
                {(["Car", "Bike", "Scooter"] as Vehicle[]).map((item) => (
                  <button key={item} className={vehicle === item ? "active" : ""} onClick={() => setVehicle(item)}>
                    <span>{item === "Car" ? "▱" : item === "Bike" ? "◇" : "◒"}</span>{item}
                  </button>
                ))}
              </div>

              <div className="problem-grid">
                {problems.map((item) => (
                  <button key={item.label} className={problem === item.label ? "problem active" : "problem"} onClick={() => setProblem(item.label)}>
                    <span className="problem-icon">{item.icon}</span>
                    <span><b>{item.label}</b><small>{item.hint}</small></span>
                    <i>✓</i>
                  </button>
                ))}
              </div>

              <button className="location-row" onClick={useMyLocation}>
                <span className="pin">●</span>
                <span><small>Your location</small><b>{locationLabel}</b></span>
                <span className="edit">Use live</span>
              </button>

              <button className="primary" onClick={startSearch}>
                Find nearby bhai <span>→</span>
              </button>
              <p className="fine">Secure nearby broadcast · Your personal contact stays hidden</p>
            </div>
          )}

          {stage === "matching" && (
            <div className="matching">
              <div className="radar">
                <span className="radar-ring one" />
                <span className="radar-ring two" />
                <span className="car-dot">▱</span>
                <span className="helper-dot h1">●</span>
                <span className="helper-dot h2">●</span>
                <span className="helper-dot h3">●</span>
              </div>
              <span className="step">LIVE BROADCAST · WITHIN 8 KM</span>
              <h2>Nearby mistris are being notified…</h2>
              <p>
                Every online mistri nearby sees your <b>{problem}</b> on a <b>{vehicle}</b>, distance and can send a price.
                First accept locks the job — numbers stay hidden until you both agree.
              </p>
              <div className="dispatch-pills">
                <span>● {vehicle}</span>
                <span>{problem}</span>
                <span>Price after accept</span>
              </div>
              <div className="search-line"><i /></div>
              <button className="cancel" disabled={cancelBusy} onClick={() => void cancelCurrentRequest("customer_cancelled_while_broadcasting")}>
                {cancelBusy ? "Cancelling…" : "Cancel request"}
              </button>
            </div>
          )}

          {stage === "accepted" && (
            <div className="quotes accepted-card">
              <div className="panel-top">
                <div>
                  <span className="step">A VERIFIED BHAI ACCEPTED</span>
                  <h2>Confirm before contact unlocks</h2>
                </div>
                <button className="back" onClick={() => void cancelCurrentRequest("customer_backed_out_before_confirm")} aria-label="Cancel">←</button>
              </div>
              <div className="accepted-helper">
                <span className="avatar large" style={{ background: activeHelper.color }}>{activeHelper.initials}</span>
                <span className="helper-info">
                  <b>{activeHelper.name} <em>✓ Verified</em></b>
                  <small>★ {activeHelper.rating} · {activeHelper.jobs} jobs</small>
                </span>
                <span className="quote-price"><strong>₹{activeHelper.price}</strong><small>price locked</small></span>
              </div>
              <div className="trip-insights">
                <span><small>Current distance</small><b>{activeHelper.distance}</b></span>
                <span><small>Estimated arrival</small><b>{activeHelper.eta}</b></span>
                <span><small>Vehicle</small><b>{vehicle}</b></span>
              </div>
              <div className="live-map-wrap">
                <MapView you={coordinates} mistri={mechanicCoordinates} height={180} />
              </div>
              <div className="anti-bypass"><span>⌾</span><p><b>Protected until you confirm</b><small>Personal numbers stay hidden. Chat blocks phone, UPI, email and outside-app links. Agreeing this price is the mutual deal.</small></p></div>
              <div className="quote-actions accepted-actions">
                <button className="chat-btn" onClick={() => setChatOpen(true)}>In-app chat</button>
                <button className="primary" onClick={() => setPaymentOpen(true)}>
                  Agree ₹{activeHelper.price} & unlock <span>→</span>
                </button>
              </div>
            </div>
          )}

          {stage === "tracking" && (
            <div className="tracking">
              <JobStepGuide
                guide={customerFlow}
                reach={customerReach}
                jobCode={assignedRequest?.jobCode}
                price={activeHelper.price}
                onPrimaryAction={jobDone && !payCollected ? () => {
                  if (!requestId) return;
                  if (!window.confirm(`Confirm you paid ₹${activeHelper.price} to ${activeHelper.name}? Only tap after paying.`)) return;
                  void markPaymentCollected(requestId)
                    .then(() => setToast("Payment marked — mistri will see it live"))
                    .catch(() => setToast("Could not update payment status"));
                } : undefined}
                primaryLabel={jobDone && !payCollected ? "I paid the mistri" : undefined}
              />
              <div className="live-map-wrap tracking-map">
                <MapView you={coordinates} mistri={mechanicCoordinates} height={200} />
              </div>
              <div className="arrival-card">
                <div className="arrival-head">
                  <div>
                    <span className="step">{trackCopy.kicker}</span>
                    <h2>{trackCopy.title}</h2>
                  </div>
                  <span className="status-dot">● {mechanicCoordinates ? "GPS LIVE" : "GPS WAITING"}</span>
                </div>
                <div className="live-facts live-facts-big">
                  <span><small>Distance</small><b>{displayDistance}</b></span>
                  <span><small>ETA</small><b>{displayEta}</b></span>
                  <span><small>Status</small><b>{liveStatus.replace(/_/g, " ")}</b></span>
                  {mapsUrl && <a href={mapsUrl} target="_blank" rel="noreferrer">Maps ↗</a>}
                </div>
                {jobDone && (
                  <div className="pay-chip pay-chip-big">
                    <b>₹{activeHelper.price}</b>
                    <span>{payCollected ? "Payment recorded ✓" : payLabel}</span>
                    {!payCollected ? (
                      <button
                        className="job-flow-cta flash"
                        onClick={() => {
                          if (!requestId) return;
                          if (!window.confirm(`Confirm you paid ₹${activeHelper.price} to ${activeHelper.name}? Only tap after paying.`)) return;
                          void markPaymentCollected(requestId)
                            .then(() => setToast("Payment marked — mistri will see it live"))
                            .catch(() => setToast("Could not update payment status"));
                        }}
                      >
                        I paid the mistri
                      </button>
                    ) : (
                      <button
                        className="offer-reset"
                        onClick={() => {
                          if (!requestId) return;
                          if (!window.confirm("Undo payment mark? Use if you tapped by mistake.")) return;
                          void revertPaymentCollected(requestId)
                            .then(() => setToast("Payment mark removed"))
                            .catch(() => setToast("Could not undo payment"));
                        }}
                      >
                        Undo — not paid yet
                      </button>
                    )}
                  </div>
                )}
                <div className="mechanic">
                  <span className="avatar large">{activeHelper.initials}</span>
                  <span><b>{activeHelper.name}</b><small>★ {activeHelper.rating} · Verified partner · ₹{activeHelper.price}</small></span>
                  <button onClick={() => setChatOpen(true)}>Chat</button>
                  {relayPhone
                    ? <a href={`tel:${relayPhone}`} aria-label="Secure relay call">Secure call</a>
                    : partnerContact
                      ? <a href={`tel:${partnerContact.phone}`} aria-label="Call mistri">{partnerContact.phone}</a>
                      : <button disabled title="Waiting for mistri to share number">Number pending</button>}
                </div>
                {numbersUnlocked && !jobDone && (
                  <div className="contact-unlock">
                    {myContact
                      ? <span className="phone-chip mine">Your number shared ✓ {myContact.phone}</span>
                      : (
                        <span className="share-row">
                          <input inputMode="tel" placeholder="Your 10-digit number" value={phoneForm} onChange={(e) => setPhoneForm(e.target.value)} />
                          <button onClick={() => void shareCustomerPhone()}>Share my number</button>
                        </span>
                      )}
                    <p className="relay-note">Numbers unlock only after both sides agree the in-app price.</p>
                  </div>
                )}
                <div className="progress" data-step={progressStep}><i /><i /><i /><i /></div>
                <div className="progress-labels"><span>Booked</span><span>On the way</span><span>Arrived</span><span>Fixed</span></div>
                {jobDone ? (
                  <button className="primary" onClick={goHome}>Back to home <span>→</span></button>
                ) : (
                  <button className="cancel" disabled={cancelBusy} onClick={() => void cancelCurrentRequest("customer_cancelled_after_confirm")}>
                    {cancelBusy ? "Cancelling…" : "Cancel booking"}
                  </button>
                )}
                <button className="text-action" onClick={() => setSupportOpen(true)}>Need support instead?</button>
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="how" id="how">
        <div><span className="section-kicker">ROAD HELP, REIMAGINED</span><h2>Three taps. Zero tension.</h2></div>
        <div className="steps">
          <article><span>01</span><div className="step-art locate"><i /><b>●</b></div><h3>Tell us what happened</h3><p>Pick your vehicle and issue. Your live location does the rest.</p></article>
          <article><span>02</span><div className="step-art quote-art"><b>₹349</b><i>Locked</i></div><h3>Nearby bhai accepts</h3><p>Online mistris nearby get vehicle, issue, distance and quote. First accept locks ETA and price.</p></article>
          <article><span>03</span><div className="step-art track-art"><i /><b>▰</b></div><h3>Agree & track live</h3><p>Confirm the price to unlock numbers, then both share live GPS on the map while the job runs in-app.</p></article>
        </div>
      </section>

      <section className="safety" id="safety">
        <div className="safety-copy">
          <span className="section-kicker">SAFE BY DESIGN</span>
          <h2>Your safety is<br />non-negotiable.</h2>
          <p>Every Mistry Bhai partner is background-verified. Every trip is tracked. Every price is recorded.</p>
          <ul>
            <li><span>✓</span> Government ID & shop verification</li>
            <li><span>✓</span> Share live status with family</li>
            <li><span>✓</span> Priority safety case & 112 fallback</li>
          </ul>
          <button className="safety-policy" onClick={() => setPolicyOpen("safety")}>Read the full Safety Standard →</button>
        </div>
        <div className="safety-card">
          <div className="shield">✓</div>
          <div><small>MISTRY BHAI PROTECT</small><strong>Every booking,<br />fully protected.</strong></div>
          <div className="protect-grid">
            <span><b>4-step</b><small>partner verification</small></span>
            <span><b>Logged</b><small>price & service record</small></span>
          </div>
        </div>
      </section>

      <footer>
        <a className="brand" href="#" aria-label="Mistry Bhai home" onClick={(event) => { event.preventDefault(); goHome(); }}>
          <span className="brand-mark"><span>MB</span></span>
          <span>Mistry <b>Bhai</b></span>
        </a>
        <p>Roadside help that feels like family.</p>
        <div>
          <button onClick={() => setPolicyOpen("terms")}>Terms</button>
          <button onClick={() => setPolicyOpen("privacy")}>Privacy</button>
          <button onClick={() => setPolicyOpen("cancellation")}>Refunds</button>
          <button onClick={() => setSupportOpen(true)}>Support</button>
        </div>
        <small>© 2026 Mistry Bhai. Made for Indian roads.</small>
      </footer>

      {chatOpen && (
        <div className="chat-overlay" role="dialog" aria-modal="true" aria-label="Chat with mechanic">
          <button className="chat-backdrop" aria-label="Close chat" onClick={() => setChatOpen(false)} />
          <section className="chat-panel">
            <header><div className="avatar">{activeHelper.initials}</div><div><b>{activeHelper.name}</b><small><span /> Protected in-app chat</small></div><button onClick={() => setChatOpen(false)}>×</button></header>
            <div className="messages">
              <div className="day">TODAY</div>
              {messages.map((msg, i) => <p key={i} className={msg.mine ? "mine" : ""}>{msg.text}<small>{msg.mine ? "Sent" : "10:42 PM"}</small></p>)}
              <div className="quick-replies">
                <button onClick={() => setMessage("Please come quickly, I am on the roadside.")}>Come quickly</button>
                <button onClick={() => setMessage("I have a spare tyre.")}>Have stepney</button>
              </div>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); sendMessage(); }}>
              <input aria-label="Type a message" placeholder="No phone, UPI, email or links" value={message} onChange={(e) => setMessage(e.target.value)} />
              <button type="submit" aria-label="Send message">→</button>
            </form>
            <small className="chat-guard">Messages stay in the booking record. Contact-detail sharing is blocked.</small>
          </section>
        </div>
      )}

      {authOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Mistry Bhai account">
          <button className="modal-backdrop" aria-label="Close account" onClick={() => setAuthOpen(false)} />
          <section className="account-modal">
            <button className="modal-close" aria-label="Close" onClick={() => setAuthOpen(false)}>×</button>
            <span className="brand-mark"><span>MB</span></span>
            {user && !user.isAnonymous ? (
              <>
                <span className="section-kicker">ACCOUNT PROTECTED</span>
                <h2>Namaste, {user.displayName || user.email?.split("@")[0]}</h2>
                <p>
                  {userRole === "partner"
                    ? "New customer alerts and your visit history live on the Mistri dashboard — not on this home page."
                    : "Your bookings, chats and safety records sync securely across devices."}
                </p>
                <div className="account-summary">
                  <span><small>Signed in as</small><b>{user.email}</b></span>
                  <span><small>Role</small><b>{userRole}</b></span>
                  {isStaffRole(userRole) && (
                    <button className="security-action" onClick={() => { setAuthOpen(false); setAppMode("admin"); }}>Open admin desk</button>
                  )}
                  <span><small>Email security</small><b className={user.emailVerified ? "verified-text" : "pending-text"}>{user.emailVerified ? "✓ Verified" : "Verification pending"}</b></span>
                </div>
                {!user.emailVerified && (
                  <button className="security-action" onClick={async () => {
                    try {
                      await sendVerificationEmail();
                      const refreshed = await refreshCurrentUser();
                      if (refreshed) setUser(refreshed);
                      setToast("Verification email sent");
                    } catch {
                      setToast("Please wait before sending another verification email");
                    }
                  }}>Send verification email</button>
                )}
                {userRole === "partner" ? (
                  <>
                    <button className="primary" onClick={() => { setAuthOpen(false); setAppMode("partner"); }}>
                      Open Mistri dashboard <span>→</span>
                    </button>
                    <button className="text-action" onClick={() => { setAuthOpen(false); void openTrips(); }}>View my customer trips</button>
                  </>
                ) : (
                  <button className="primary" onClick={() => { setAuthOpen(false); void openTrips(); }}>View my trips <span>→</span></button>
                )}
                <button className="text-action" onClick={() => { setAuthOpen(false); setPolicyOpen("privacy"); }}>Privacy & account controls</button>
                <button className="text-action" onClick={async () => { await logoutUser(); setAuthOpen(false); setToast("Signed out safely"); }}>Sign out</button>
              </>
            ) : (
              <>
                <span className="section-kicker">SAVE YOUR EMERGENCY HISTORY</span>
                <h2>{authMode === "signup" ? "Create your account" : "Welcome back"}</h2>
                <p>Continue as guest anytime, or sign in to keep trips and invoices on every device.</p>
                <button className="google-button" disabled={authBusy} onClick={googleAuth}><b>G</b> Continue with Google</button>
                <div className="or"><span />or use email<span /></div>
                <label>Email address<input type="email" autoComplete="email" value={authEmail} onChange={(e) => setAuthEmail(e.target.value)} placeholder="you@example.com" /></label>
                <label>Password<input type="password" autoComplete={authMode === "signup" ? "new-password" : "current-password"} value={authPassword} onChange={(e) => setAuthPassword(e.target.value)} placeholder={authMode === "signup" ? "8+ with upper, lower & number" : "Your password"} /></label>
                {authMode === "signup" && (
                  <label className="check-row">
                    <input type="checkbox" checked={signupConsent} onChange={(e) => setSignupConsent(e.target.checked)} />
                    <span>I agree to the <button type="button" onClick={() => { setAuthOpen(false); setPolicyOpen("terms"); }}>Terms</button> and <button type="button" onClick={() => { setAuthOpen(false); setPolicyOpen("privacy"); }}>Privacy Notice</button> (v{POLICY_VERSION}).</span>
                  </label>
                )}
                <button className="primary" disabled={authBusy} onClick={submitAuth}>{authBusy ? "Please wait…" : authMode === "signup" ? "Create secure account" : "Sign in"} <span>→</span></button>
                {authMode === "signin" && <button className="reset-link" onClick={async () => { if (!authEmail) return setToast("Enter your email first"); await resetPassword(authEmail); setToast("Password reset email sent"); }}>Forgot password?</button>}
                <button className="text-action" onClick={() => setAuthMode(authMode === "signup" ? "signin" : "signup")}>
                  {authMode === "signup" ? "Already have an account? Sign in" : "New here? Create an account"}
                </button>
                <button className="guest-action" onClick={() => setAuthOpen(false)}>Continue as guest</button>
              </>
            )}
          </section>
        </div>
      )}

      {tripsOpen && (
        <div className="drawer-overlay" role="dialog" aria-modal="true" aria-label="My trips">
          <button className="modal-backdrop" aria-label="Close trips" onClick={() => setTripsOpen(false)} />
          <aside className="trips-drawer">
            <header><div><span className="section-kicker">FIREBASE-SYNCED</span><h2>My trips</h2></div><button onClick={() => setTripsOpen(false)}>×</button></header>
            <div className="trip-list">
              {trips.length ? trips.map((trip) => {
                const actionable = tripNeedsAction(trip.status);
                const hint = tripActionHint(trip);
                return (
                  <article
                    key={trip.id}
                    className={actionable ? "trip-open" : "trip-muted"}
                    role={actionable ? "button" : undefined}
                    tabIndex={actionable ? 0 : undefined}
                    onClick={actionable ? () => void resumeTrip(trip) : undefined}
                    onKeyDown={actionable ? (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        void resumeTrip(trip);
                      }
                    } : undefined}
                  >
                    <span className="trip-icon">{trip.vehicle === "Car" ? "▱" : "◇"}</span>
                    <div>
                      <b>{trip.problem} · {trip.vehicle}</b>
                      <small>{trip.locationLabel}</small>
                      <em>{trip.helperName || "Finding a nearby bhai"}</em>
                    </div>
                    <span className={`trip-status ${trip.status}`}>{formatTripStatus(trip.status)}</span>
                    {trip.price && <strong>₹{trip.price}</strong>}
                    {hint && <span className="trip-action">{hint}</span>}
                  </article>
                );
              }) : (
                <div className="empty-trips"><span>⌁</span><h3>No saved trips yet</h3><p>Your next roadside request will appear here in real time.</p><button className="primary" onClick={() => setTripsOpen(false)}>Book roadside help</button></div>
              )}
            </div>
            {user?.isAnonymous && <div className="drawer-signin"><p>Sign in to keep trips across every device.</p><button onClick={() => { setTripsOpen(false); setAuthOpen(true); }}>Protect my trips</button></div>}
          </aside>
        </div>
      )}

      {partnerOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Partner application">
          <button className="modal-backdrop" aria-label="Close partner application" onClick={() => setPartnerOpen(false)} />
          <section className="partner-modal partner-kyc-modal">
            <button className="modal-close" aria-label="Close" onClick={() => setPartnerOpen(false)}>×</button>
            <span className="section-kicker">MISTRY BHAI PARTNER KYC</span>
            <h2>Earn more. Help nearby.</h2>
            <p>Full verification like other ride/help apps — identity, address, payout and documents are reviewed by admin before you go online.</p>
            {!user || user.isAnonymous ? (
              <div className="dash-card" style={{ marginBottom: 12 }}>
                <p>Sign in with Google or email first. Guest accounts cannot submit partner KYC.</p>
                <button className="primary" onClick={() => { setPartnerOpen(false); setAuthOpen(true); }}>Sign in to continue <span>→</span></button>
              </div>
            ) : (
              <>
                <div className="kyc-section"><h3>1. Personal details</h3>
                  <label>Full legal name<input value={partnerForm.name} onChange={(e) => setPartnerForm({ ...partnerForm, name: e.target.value })} placeholder="As on Aadhaar / DL" /></label>
                  <label>Date of birth<input type="date" value={partnerForm.dateOfBirth} onChange={(e) => setPartnerForm({ ...partnerForm, dateOfBirth: e.target.value })} /></label>
                  <label>Phone number<input inputMode="tel" value={partnerForm.phone} onChange={(e) => setPartnerForm({ ...partnerForm, phone: e.target.value })} placeholder="+91 98765 43210" /></label>
                  <label>Alternate phone<input inputMode="tel" value={partnerForm.alternatePhone} onChange={(e) => setPartnerForm({ ...partnerForm, alternatePhone: e.target.value })} placeholder="Optional" /></label>
                  <label>Email<input type="email" value={partnerForm.email} onChange={(e) => setPartnerForm({ ...partnerForm, email: e.target.value })} placeholder={user.email || "you@example.com"} /></label>
                </div>

                <div className="kyc-section"><h3>2. Address</h3>
                  <AddressAutocomplete
                    value={{
                      addressLine: partnerForm.addressLine,
                      city: partnerForm.city,
                      state: partnerForm.state,
                      pincode: partnerForm.pincode,
                    }}
                    onChange={(next) => setPartnerForm((prev) => ({
                      ...prev,
                      addressLine: next.addressLine,
                      city: next.city,
                      state: next.state,
                      pincode: next.pincode,
                    }))}
                  />
                </div>

                <div className="kyc-section"><h3>3. Work profile</h3>
                  <label>Shop / work name<input value={partnerForm.shopName} onChange={(e) => setPartnerForm({ ...partnerForm, shopName: e.target.value })} placeholder="Ravi Tyre Care" /></label>
                  <label>Experience (years)<input type="number" min="0" max="60" value={partnerForm.experienceYears} onChange={(e) => setPartnerForm({ ...partnerForm, experienceYears: e.target.value })} /></label>
                  <label>Work type
                    <select value={partnerForm.workType} onChange={(e) => setPartnerForm({ ...partnerForm, workType: e.target.value as "mobile" | "workshop" | "both" })}>
                      <option value="mobile">Mobile roadside</option>
                      <option value="workshop">Workshop based</option>
                      <option value="both">Both</option>
                    </select>
                  </label>
                  <label>Service radius (km)<input type="number" min="3" max="40" value={partnerForm.serviceRadiusKm} onChange={(e) => setPartnerForm({ ...partnerForm, serviceRadiusKm: e.target.value })} /></label>
                  <label>GSTIN (optional)<input value={partnerForm.gstin} onChange={(e) => setPartnerForm({ ...partnerForm, gstin: e.target.value })} placeholder="If registered" /></label>
                  <fieldset className="kyc-chips">
                    <legend>Services</legend>
                    {SERVICE_OPTIONS.map((item) => (
                      <label key={item} className="chip-check">
                        <input
                          type="checkbox"
                          checked={partnerForm.services.includes(item)}
                          onChange={(e) => setPartnerForm({
                            ...partnerForm,
                            services: e.target.checked
                              ? [...partnerForm.services, item]
                              : partnerForm.services.filter((s) => s !== item),
                          })}
                        />
                        {item}
                      </label>
                    ))}
                  </fieldset>
                  <fieldset className="kyc-chips">
                    <legend>Vehicles you serve</legend>
                    {VEHICLE_OPTIONS.map((item) => (
                      <label key={item} className="chip-check">
                        <input
                          type="checkbox"
                          checked={partnerForm.vehicleTypes.includes(item)}
                          onChange={(e) => setPartnerForm({
                            ...partnerForm,
                            vehicleTypes: e.target.checked
                              ? [...partnerForm.vehicleTypes, item]
                              : partnerForm.vehicleTypes.filter((s) => s !== item),
                          })}
                        />
                        {item}
                      </label>
                    ))}
                  </fieldset>
                </div>

                <div className="kyc-section"><h3>4. Identity documents</h3>
                  <label>ID type
                    <select value={partnerForm.idType} onChange={(e) => setPartnerForm({ ...partnerForm, idType: e.target.value as typeof partnerForm.idType })}>
                      <option value="aadhaar">Aadhaar</option>
                      <option value="dl">Driving licence</option>
                      <option value="voter">Voter ID</option>
                      <option value="pan">PAN</option>
                    </select>
                  </label>
                  <label>ID number<input value={partnerForm.idNumber} onChange={(e) => setPartnerForm({ ...partnerForm, idNumber: e.target.value })} placeholder={partnerForm.idType === "aadhaar" ? "Only last 4 stored as XXXX-XXXX-1234" : "As on document"} /></label>
                  <label>PAN (recommended)<input value={partnerForm.panNumber} onChange={(e) => setPartnerForm({ ...partnerForm, panNumber: e.target.value })} placeholder="ABCDE1234F" /></label>
                  <label>Driving licence no.<input value={partnerForm.dlNumber} onChange={(e) => setPartnerForm({ ...partnerForm, dlNumber: e.target.value })} placeholder="If mobile mechanic" /></label>
                  <KycMediaField
                    label="Government ID photo / PDF"
                    hint="Required · max 8 MB · Attach file or Capture photo"
                    allowPdf
                    required
                    value={idDocMedia}
                    onChange={setIdDocMedia}
                  />
                  <KycMediaField
                    label="Live selfie"
                    hint="Required · tap Capture photo, then Take photo"
                    requireCamera
                    required
                    value={selfieMedia}
                    onChange={setSelfieMedia}
                  />
                  <KycMediaField
                    label="Workshop / van photo (optional)"
                    hint="Optional · Attach or capture"
                    value={workshopMedia}
                    onChange={setWorkshopMedia}
                  />
                </div>

                <div className="kyc-section"><h3>5. Payout details</h3>
                  <label>Account holder name<input value={partnerForm.accountHolderName} onChange={(e) => setPartnerForm({ ...partnerForm, accountHolderName: e.target.value })} /></label>
                  <label>Bank account number<input inputMode="numeric" value={partnerForm.accountNumber} onChange={(e) => setPartnerForm({ ...partnerForm, accountNumber: e.target.value })} /></label>
                  <label>IFSC<input value={partnerForm.ifsc} onChange={(e) => setPartnerForm({ ...partnerForm, ifsc: e.target.value.toUpperCase() })} placeholder="SBIN0001234" /></label>
                  <label>UPI ID
                    <input
                      value={partnerForm.upiId}
                      onChange={(e) => {
                        const next = e.target.value;
                        setPartnerForm({ ...partnerForm, upiId: next });
                        const normalized = normalizeUpiId(next);
                        if (!normalized) setUpiHint("");
                        else if (isValidUpiId(normalized)) setUpiHint("✓ Valid UPI format");
                        else setUpiHint("Use format like name@ybl / name@paytm / 98xxxxxxxx@oksbi");
                      }}
                      onBlur={() => setPartnerForm((prev) => ({ ...prev, upiId: normalizeUpiId(prev.upiId) }))}
                      placeholder="yourname@ybl"
                      autoComplete="off"
                      inputMode="email"
                    />
                    <small className={upiHint.startsWith("✓") ? "upi-ok" : "upi-bad"}>{upiHint || "Required for payouts · auto-validated before approval"}</small>
                  </label>
                  <small>Account number is stored masked. Never share full bank details in chat.</small>
                </div>

                <div className="kyc-section"><h3>6. Emergency contact</h3>
                  <label>Contact name<input value={partnerForm.emergencyName} onChange={(e) => setPartnerForm({ ...partnerForm, emergencyName: e.target.value })} /></label>
                  <label>Contact phone<input inputMode="tel" value={partnerForm.emergencyPhone} onChange={(e) => setPartnerForm({ ...partnerForm, emergencyPhone: e.target.value })} /></label>
                </div>

                <div className="partner-benefits"><span>✓ Auto secure checks</span><span>✓ Live selfie required</span><span>✓ UPI validated</span></div>
                <label className="check-row">
                  <input type="checkbox" checked={partnerForm.acceptedPartnerCode} onChange={(e) => setPartnerForm({ ...partnerForm, acceptedPartnerCode: e.target.checked })} />
                  <span>I confirm details are true and accept identity checks + the <button type="button" onClick={() => { setPartnerOpen(false); setPolicyOpen("partner"); }}>Partner Code</button>.</span>
                </label>
                {kycFailList.length > 0 && (
                  <div className="kyc-fail-list">
                    <b>Cannot auto-approve yet</b>
                    <ul>
                      {kycFailList.map((item) => <li key={item}>{item}</li>)}
                    </ul>
                    <small>Government ID: scroll up to section 4 and use Attach file or Capture photo until a preview appears.</small>
                  </div>
                )}
                <div className="kyc-submit-row">
                  <button type="button" className="media-btn" onClick={savePartnerDraft}>Save draft</button>
                  <button className="primary" disabled={partnerBusy} onClick={applyAsPartner}>
                    {partnerBusy ? "Verifying & uploading…" : "Verify & auto-approve"} <span>→</span>
                  </button>
                </div>
              </>
            )}
            <button className="text-action" onClick={() => { setPartnerOpen(false); setAppMode("partner"); }}>
              Already approved? Open mistri dashboard →
            </button>
            <small>When all checks pass (including live camera selfie + valid UPI), approval is automatic and secure. Admin can still suspend later.</small>
          </section>
        </div>
      )}

      {paymentOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Confirm booking and payment">
          <button className="modal-backdrop" aria-label="Close checkout" onClick={() => setPaymentOpen(false)} />
          <section className="checkout-modal">
            <button className="modal-close" aria-label="Close" onClick={() => setPaymentOpen(false)}>×</button>
            <span className="section-kicker">PROTECTED CHECKOUT</span>
            <h2>Confirm ₹{activeHelper.price}</h2>
            <p>Your price is recorded before dispatch. Parts or extra work need your approval in chat.</p>
            <div className="price-breakdown">
              <span><small>{problem} visit + labour</small><b>₹{activeHelper.price}</b></span>
              <span><small>Platform emergency fee</small><b>₹0</b></span>
              <span className="total"><small>Total due after service</small><b>₹{activeHelper.price}</b></span>
            </div>
            <fieldset className="payment-methods">
              <legend>Choose payment</legend>
              <label className={paymentMethod === "upi_on_service" ? "selected" : ""}>
                <input type="radio" name="payment" checked={paymentMethod === "upi_on_service"} onChange={() => setPaymentMethod("upi_on_service")} />
                <span><b>UPI after service</b><small>Pay the verified partner only after the job</small></span>
                <em>Recommended</em>
              </label>
              <label className={paymentMethod === "cash" ? "selected" : ""}>
                <input type="radio" name="payment" checked={paymentMethod === "cash"} onChange={() => setPaymentMethod("cash")} />
                <span><b>Cash after service</b><small>Collect the digital service record</small></span>
              </label>
              <label className={onlinePaymentsConfigured && paymentMethod === "razorpay" ? "selected" : onlinePaymentsConfigured ? "" : "disabled"}>
                <input type="radio" name="payment" disabled={!onlinePaymentsConfigured} checked={paymentMethod === "razorpay"} onChange={() => setPaymentMethod("razorpay")} />
                <span><b>Prepaid UPI / card</b><small>{onlinePaymentsConfigured ? "Protected by server order, signature and webhook verification" : "Activation pending merchant KYC, App Check and secure server keys"}</small></span>
                <em>{onlinePaymentsConfigured ? "Secure" : "Pending"}</em>
              </label>
            </fieldset>
            <label className="check-row">
              <input type="checkbox" checked={bookingConsent} onChange={(e) => setBookingConsent(e.target.checked)} />
              <span>I accept the <button type="button" onClick={() => { setPaymentOpen(false); setPolicyOpen("terms"); }}>Booking Terms</button> and <button type="button" onClick={() => { setPaymentOpen(false); setPolicyOpen("cancellation"); }}>Cancellation Policy</button>.</span>
            </label>
            <button className="primary" onClick={bookHelper}>Confirm & dispatch bhai <span>→</span></button>
            <small className="secure-note">🔒 Payment status and consent are stored in your protected Firebase booking record.</small>
          </section>
        </div>
      )}

      {policyOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-label={policyContent[policyOpen].title}>
          <button className="modal-backdrop" aria-label="Close policy" onClick={() => setPolicyOpen(null)} />
          <section className="policy-modal">
            <button className="modal-close" aria-label="Close" onClick={() => setPolicyOpen(null)}>×</button>
            <span className="section-kicker">{policyContent[policyOpen].eyebrow}</span>
            <h2>{policyContent[policyOpen].title}</h2>
            <p className="policy-version">Effective {POLICY_VERSION} · Plain-language operating policy</p>
            <div className="policy-sections">
              {policyContent[policyOpen].sections.map((section) => (
                <article key={section.heading}><h3>{section.heading}</h3><p>{section.text}</p></article>
              ))}
            </div>
            {policyOpen === "privacy" && (
              <div className="privacy-controls">
                <h3>Your data rights</h3>
                <p>Submit a verified request. Support will confirm identity before releasing, correcting or deleting account data.</p>
                <div>
                  <button disabled={privacyBusy} onClick={() => void requestPrivacyAction("access")}>Access</button>
                  <button disabled={privacyBusy} onClick={() => void requestPrivacyAction("correction")}>Correct</button>
                  <button disabled={privacyBusy} onClick={() => void requestPrivacyAction("deletion")}>Delete</button>
                </div>
              </div>
            )}
            <div className="policy-contact">
              <span><b>Grievance & privacy contact</b><small>4thisis4it@gmail.com</small></span>
              <button onClick={() => { setPolicyOpen(null); setSupportOpen(true); }}>Raise a case</button>
            </div>
            <small className="legal-note">These product policies must be reviewed by qualified Indian legal and tax professionals before commercial launch.</small>
          </section>
        </div>
      )}

      {supportOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Support and grievance">
          <button className="modal-backdrop" aria-label="Close support" onClick={() => setSupportOpen(false)} />
          <section className="support-modal">
            <button className="modal-close" aria-label="Close" onClick={() => setSupportOpen(false)}>×</button>
            <span className="section-kicker">SUPPORT & GRIEVANCE</span>
            <h2>Tell us what went wrong</h2>
            <p>Safety concerns are marked urgent. If anyone is in immediate danger, call 112.</p>
            <label>Category
              <select value={supportForm.category} onChange={(e) => setSupportForm({ ...supportForm, category: e.target.value })}>
                <option value="booking">Booking or price</option>
                <option value="payment">Payment or refund</option>
                <option value="partner">Partner conduct</option>
                <option value="privacy">Privacy or account</option>
                <option value="safety">Urgent safety concern</option>
              </select>
            </label>
            <label>What happened?
              <textarea rows={5} maxLength={1200} value={supportForm.message} onChange={(e) => setSupportForm({ ...supportForm, message: e.target.value })} placeholder="Include the booking, amount and outcome you need…" />
            </label>
            <button className="primary" onClick={createSupportCase}>Create support case <span>→</span></button>
            <small>Email fallback: <a href="mailto:4thisis4it@gmail.com">4thisis4it@gmail.com</a></small>
          </section>
        </div>
      )}

      {toast && <div className="toast"><span>✓</span>{toast}</div>}
      <PwaInstallBanner />
    </main>
  );
}
